import { z } from 'zod';
import {
  getNonNullFilterWithType,
  getSingleValueField,
  getMultipleValueField,
  isMultiValueType,
  getDateRangeValueField,
} from '../tableConditionalFilter/conditionalFiltersUseCase';
import { parseApiDateString } from '../globalFilters/globalFiltersUseCase';

const TIME_REGEX = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;

/**
 * @param {ConditionalFilter} filter
 * @returns {ApiManagerGlobalFiltersOperator | ''}
 */
export const getSidebarFilterOperator = (filter) => {
  const operatorOptions = filter?.operatorOptions;
  if (!operatorOptions?.length) {
    return 'is';
  }

  if (operatorOptions.length > 1 && process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(
      `[calendarSidebarFilters] Filter "${filter.id}" has multiple operator options; using "${operatorOptions[0]}".`,
    );
  }

  return operatorOptions[0];
};

/**
 * @param {ApiManagerGlobalFiltersCreateValue} row
 * @param {Record<string, ConditionalFilter>} filtersById
 * @returns {boolean}
 */
export const isSidebarFilterValueEmpty = (row, filtersById) => {
  if (!row?.id) return true;

  const type = filtersById[row.id]?.type;
  const values = row.values ?? [];

  switch (type) {
    case 'select_multiple':
      return getMultipleValueField(values).length === 0;
    case 'dateRange': {
      const { start, end } = getDateRangeValueField(values);
      return !start || !end;
    }
    default: {
      const value = getSingleValueField(values);
      return !value || (typeof value === 'string' && value.trim() === '');
    }
  }
};

/**
 * @param {Array<ApiManagerGlobalFiltersFilter>} tableFilters
 * @param {Array<ApiManagerFilterValues>} [filterValues]
 * @returns {ApiManagerGlobalFiltersCreateValue[]}
 */
export const buildSidebarFilterRows = (tableFilters, filterValues = []) => {
  const valuesById = (filterValues ?? []).reduce((acc, filterValue) => {
    if (filterValue?.id) {
      acc[filterValue.id] = filterValue;
    }
    return acc;
  }, /** @type {Record<string, ApiManagerFilterValues>} */ ({}));

  return (tableFilters ?? [])
    .map((apiFilter) => getNonNullFilterWithType(apiFilter))
    .filter(Boolean)
    .map((filter) => {
      const existing = valuesById[filter.id];
      const operator = getSidebarFilterOperator(filter);

      return {
        id: filter.id,
        operator,
        values: existing?.values ?? [],
        singleValue: !isMultiValueType(filter.type),
      };
    });
};

/**
 * @param {ApiManagerGlobalFiltersCreateValue[]} rows
 * @param {Record<string, ConditionalFilter>} filtersById
 * @param {Array<ApiManagerFilterValues>} [filterValues]
 * @returns {Array<ApiManagerFilterValues>}
 */
export const buildSidebarFiltersPayload = (rows, filtersById, filterValues = []) => {
  const sidebarIds = new Set(Object.keys(filtersById ?? {}));

  const fromRows = (rows ?? [])
    .filter((row) => row?.id && row?.operator)
    .map((row) => ({
      id: row.id,
      operator: row.operator,
      values: row.values ?? [],
      singleValue: row.singleValue,
    }));

  const passThrough = (filterValues ?? []).filter(
    (filterValue) => filterValue?.id && !sidebarIds.has(filterValue.id),
  );

  return [...fromRows, ...passThrough];
};

/**
 * @param {Record<string, ConditionalFilter>} filtersById
 * @returns {z.ZodType}
 */
export const createSidebarValidationSchema = (filtersById) => {
  const baseSchema = z.object({
    id: z.string().optional(),
    operator: z.string().optional(),
    values: z.array(z.string()).default([]),
    singleValue: z.boolean().default(true),
  })
    .refine(
      (data) => {
        if (!data.id) return true;
        return data.id.trim().length > 0;
      },
      {
        message: 'conditionalFilters.id.required',
        path: ['id'],
      },
    )
    .refine(
      (data) => {
        if (!data.id || !data.id.trim()) return true;
        return Boolean(data.operator && data.operator.trim().length > 0);
      },
      {
        message: 'conditionalFilters.operator.required',
        path: ['operator'],
      },
    )
    .refine(
      (data) => {
        if (!data.id || !data.operator) return true;
        const filter = filtersById[data.id];
        if (filter?.operatorOptions?.length) {
          const { operatorOptions } = filter;
          /** @type {Array<string>} */
          const options = operatorOptions;
          return options.includes(data.operator);
        }
        return true;
      },
      {
        message: 'conditionalFilters.operator.invalid',
        path: ['operator'],
      },
    )
    .refine(
      (data) => {
        if (!data.id || !data.operator) return true;
        const filter = filtersById[data.id];
        if (!filter?.required) return true;
        return !isSidebarFilterValueEmpty(
          /** @type {ApiManagerGlobalFiltersCreateValue} */ (data),
          filtersById,
        );
      },
      {
        message: 'conditionalFilters.value.required',
        path: ['values'],
      },
    )
    .refine(
      (data) => {
        if (!data.id || !data.operator) return true;
        const type = filtersById[data.id]?.type;
        if (type !== 'text') return true;
        if (isSidebarFilterValueEmpty(
          /** @type {ApiManagerGlobalFiltersCreateValue} */ (data),
          filtersById,
        )) return true;

        const value = getSingleValueField(data.values);
        return typeof value === 'string';
      },
      {
        message: 'conditionalFilters.text.required',
        path: ['values'],
      },
    )
    .refine(
      (data) => {
        if (!data.id || !data.operator) return true;
        const type = filtersById[data.id]?.type;
        if (type !== 'date') return true;
        if (isSidebarFilterValueEmpty(
          /** @type {ApiManagerGlobalFiltersCreateValue} */ (data),
          filtersById,
        )) return true;

        const value = getSingleValueField(data.values);
        const date = new Date(value);
        return date instanceof Date && !Number.isNaN(date.getTime());
      },
      {
        message: 'conditionalFilters.date.required',
        path: ['values'],
      },
    )
    .refine(
      (data) => {
        if (!data.id || !data.operator) return true;
        const type = filtersById[data.id]?.type;
        if (type !== 'time') return true;
        if (isSidebarFilterValueEmpty(
          /** @type {ApiManagerGlobalFiltersCreateValue} */ (data),
          filtersById,
        )) return true;

        const value = getSingleValueField(data.values);
        return typeof value === 'string' && TIME_REGEX.test(value.trim());
      },
      {
        message: 'conditionalFilters.time.required',
        path: ['values'],
      },
    )
    .refine(
      (data) => {
        if (!data.id || !data.operator) return true;
        const type = filtersById[data.id]?.type;
        if (type !== 'select') return true;
        if (isSidebarFilterValueEmpty(
          /** @type {ApiManagerGlobalFiltersCreateValue} */ (data),
          filtersById,
        )) return true;

        const value = getSingleValueField(data.values);
        return typeof value === 'string' && value.trim() !== '';
      },
      {
        message: 'conditionalFilters.select.required',
        path: ['values'],
      },
    )
    .refine(
      (data) => {
        if (!data.id || !data.operator) return true;
        const type = filtersById[data.id]?.type;
        if (type !== 'select_multiple') return true;
        if (isSidebarFilterValueEmpty(
          /** @type {ApiManagerGlobalFiltersCreateValue} */ (data),
          filtersById,
        )) return true;

        const value = getMultipleValueField(data.values);
        return value.length > 0
          && value.every((item) => typeof item === 'string' && item.trim() !== '');
      },
      {
        message: 'conditionalFilters.select_multiple.required',
        path: ['values'],
      },
    )
    .refine(
      (data) => {
        if (!data.id || !data.operator) return true;
        const type = filtersById[data.id]?.type;
        if (type !== 'dateRange') return true;
        if (isSidebarFilterValueEmpty(
          /** @type {ApiManagerGlobalFiltersCreateValue} */ (data),
          filtersById,
        )) return true;

        const { start, end } = getDateRangeValueField(data.values);
        const startDate = parseApiDateString(start);
        const endDate = parseApiDateString(end);

        return Boolean(startDate && endDate);
      },
      {
        message: 'conditionalFilters.dateRange.required',
        path: ['values'],
      },
    )
    .refine(
      (data) => {
        if (!data.id || !data.operator) return true;
        const type = filtersById[data.id]?.type;
        if (type !== 'dateRange') return true;
        if (isSidebarFilterValueEmpty(
          /** @type {ApiManagerGlobalFiltersCreateValue} */ (data),
          filtersById,
        )) return true;

        const { start, end } = getDateRangeValueField(data.values);
        const startDate = parseApiDateString(start);
        const endDate = parseApiDateString(end);

        if (!startDate || !endDate) return true;

        return startDate.getTime() <= endDate.getTime();
      },
      {
        message: 'conditionalFilters.dateRange.order',
        path: ['values'],
      },
    );

  return z.object({
    conditionalFilters: z.array(baseSchema).optional().default([]),
  });
};

/**
 * @param {Array<ApiManagerFilterValues>} filterValues
 * @returns {number}
 */
export const getSidebarFiltersCount = (filterValues) => (
  filterValues?.filter(
    (filterValue) => filterValue?.values?.some((value) => value && value.trim() !== ''),
  ).length ?? 0
);
